/**
 * Touch Gestures Composable with State Machine
 * 
 * Simplified touch gesture handling using a finite state machine:
 * - IDLE → TAP_START → WAITING_DOUBLE_TAP → (DOUBLE_TAP | SINGLE_TAP)
 * - IDLE → TAP_START → LONG_PRESS
 * 
 * Replaces complex flag-based logic with clear state transitions.
 */

import { reactive, computed, type Ref } from 'vue';

type TouchState = 'idle' | 'tap_start' | 'waiting_double_tap' | 'long_press' | 'handled';

interface TouchGestureState {
  state: TouchState;
  itemId: string | null;
  startTime: number;
  touchType: 'image' | 'content';
  startPos: { x: number; y: number };
}

export interface TouchGestureOptions {
  onDoubleTap: (item: any) => void;
  onSingleTap: (item: any, touchType: 'image' | 'content') => void;
  onLongPress: (item: any) => void;
  doubleTapDelay?: number;
  longPressDelay?: number;
  moveThreshold?: number;
}

export function useTouchGestures(options: TouchGestureOptions) {
  const {
    onDoubleTap,
    onSingleTap,
    onLongPress,
    doubleTapDelay = 280,
    longPressDelay = 400,
    moveThreshold = 10
  } = options;

  const gesture = reactive<TouchGestureState>({
    state: 'idle',
    itemId: null,
    startTime: 0,
    touchType: 'content',
    startPos: { x: 0, y: 0 }
  });

  let doubleTapTimer: number | null = null;
  let longPressTimer: number | null = null;

  const reset = () => {
    gesture.state = 'idle';
    gesture.itemId = null;
    gesture.startTime = 0;

    if (doubleTapTimer) {
      clearTimeout(doubleTapTimer);
      doubleTapTimer = null;
    }
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };

  const handleStart = (item: any, e: TouchEvent, touchType: 'image' | 'content') => {
    const touch = e.touches[0];

    // If already in waiting_double_tap state and this is the same element
    if (gesture.state === 'waiting_double_tap' && gesture.itemId === String(item.id)) {
      const timeSinceFirstTap = Date.now() - gesture.startTime;

      if (timeSinceFirstTap < doubleTapDelay) {
        // Double tap detected
        gesture.state = 'handled';
        reset();
        onDoubleTap(item);
        return;
      }
    }

    // Reset previous state
    reset();

    // Start new gesture
    gesture.state = 'tap_start';
    gesture.itemId = String(item.id);
    gesture.startTime = Date.now();
    gesture.touchType = touchType;
    gesture.startPos = { x: touch.clientX, y: touch.clientY };

    // Start long press timer
    longPressTimer = window.setTimeout(() => {
      if (gesture.state === 'tap_start') {
        gesture.state = 'long_press';
        reset();
        onLongPress(item);
      }
    }, longPressDelay);
  };

  const handleMove = (e: TouchEvent) => {
    if (gesture.state !== 'tap_start') return;

    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - gesture.startPos.x);
    const dy = Math.abs(touch.clientY - gesture.startPos.y);

    // If moved beyond threshold, cancel gesture
    if (dx > moveThreshold || dy > moveThreshold) {
      reset();
    }
  };

  const handleEnd = (item: any) => {
    if (gesture.state !== 'tap_start') return;

    gesture.state = 'waiting_double_tap';

    // Wait for possible second tap
    doubleTapTimer = window.setTimeout(() => {
      if (gesture.state === 'waiting_double_tap') {
        // Single tap
        reset();
        onSingleTap(item, gesture.touchType);
      }
    }, doubleTapDelay);
  };

  const cancel = () => {
    reset();
  };

  return {
    handleStart,
    handleMove,
    handleEnd,
    cancel,
    state: computed(() => gesture.state)
  };
}
